/* ──────────────────────────────────────────────────────────────
   shell/topbar.tsx · app topbar
   Left:  Organization + Location switchers
   Right: premium profile avatar (hover → name tooltip)
   ────────────────────────────────────────────────────────────── */
import React from 'react';
import {
  View,
  TouchableOpacity,
  Platform,
  ViewStyle,
} from 'react-native';
import { useTheme } from '../context/themeContext';
import { useApp } from '../context/appContext';
import { useSession } from '../context/sessionContext';
import {
  AppDropdown,
  Icon,
} from '../components/ui';

interface TopbarProps {
  /** Show the hamburger that opens the mobile drawer. */
  showMenu?: boolean;
  onMenu?: () => void;
}

export default function Topbar({ showMenu = false, onMenu }: TopbarProps) {
  const { theme, isDark } = useTheme();
  const { colors } = theme;
  const { tenant, setTenant } = useApp();
  const { org } = useSession();

  // Real org (single tenant for now). The switcher shows the business name.
  const tenantItems = org?.id ? [{ id: org.id, name: org.name || 'My Org' }] : [tenant];

  // Desktop: single row with the org switcher (profile lives in the sidebar).
  // Mobile: hamburger + org switcher on one row (drawer holds the profile).
  const topbarStyle: ViewStyle = {
    flexShrink: 0,
    borderBottomWidth: 1,
    borderBottomColor: colors.line,
    backgroundColor: colors.card,
    // Keep above the content area so any dropdown menu isn't clipped by the
    // screen below it.
    ...(Platform.OS === 'web' ? ({ position: 'relative', zIndex: 100, overflow: 'visible' } as any) : {}),
    ...(showMenu
      ? { flexDirection: 'row', alignItems: 'center', gap: 10, paddingHorizontal: 14, paddingVertical: 10 }
      : { flexDirection: 'row', alignItems: 'center', height: 60, gap: 14, paddingHorizontal: 28, paddingVertical: 10 }),
  };

  // On mobile each switcher flexes to share the full width of its own row.
  const switcherStyle: ViewStyle = showMenu ? { flex: 1, minWidth: 0 } : { width: 184 };

  const orgDropdown = (
    <AppDropdown
      compact
      isDark={isDark}
      fieldStyle={switcherStyle}
      textColor={colors.ink}
      placeholder={org?.name || 'Organization'}
      value={org?.id ?? ''}
      items={tenantItems.map((t) => ({ label: t.name, value: t.id }))}
      onSelect={(id) => {
        const t = tenantItems.find((x) => x.id === id);
        if (t) setTenant(t);
      }}
    />
  );

  // ── Mobile: hamburger + org switcher (profile lives in the drawer) ─
  if (showMenu) {
    return (
      <View style={topbarStyle}>
        <TouchableOpacity
          onPress={onMenu}
          accessibilityLabel="Open menu"
          hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
          style={{
            width: 38,
            height: 38,
            borderRadius: 2,
            alignItems: 'center',
            justifyContent: 'center',
            borderWidth: 1,
            borderColor: colors.line,
            backgroundColor: colors.paper2,
            flexShrink: 0,
            ...(Platform.OS === 'web' ? { cursor: 'pointer' } as any : {}),
          }}
        >
          <Icon name="menu" size={18} color={colors.ink} />
        </TouchableOpacity>
        {orgDropdown}
      </View>
    );
  }

  // ── Desktop: org switcher, left-aligned (profile lives in the sidebar) ─
  return (
    <View style={topbarStyle}>
      {orgDropdown}
    </View>
  );
}
